import React from "react";

// Core app pages
export const Dashboard = React.lazy(() =>
  import("../pages/Dashboard").then((module) => ({
    default: module.Dashboard,
  })),
);
export const Meetups = React.lazy(() =>
  import("../pages/Meetups").then((module) => ({ default: module.Meetups })),
);
export const CreateMeetup = React.lazy(() => import("../pages/CreateMeetup"));
export const CreateMeetupWizard = React.lazy(() =>
  import("../pages/CreateMeetupWizard").then((module) => ({
    default: module.CreateMeetupWizard,
  })),
);
export const MeetupDetails = React.lazy(() => import("../pages/MeetupDetails"));
export const MeetupDetailEnhanced = React.lazy(
  () => import("../components/meetups/MeetupDetailEnhanced"),
);
export const EditMeetup = React.lazy(() =>
  import("../pages/EditMeetup").then((module) => ({
    default: module.EditMeetup,
  })),
);
export const Profile = React.lazy(() =>
  import("../pages/Profile").then((module) => ({ default: module.Profile })),
);

// Social pages
export const Friends = React.lazy(() =>
  import("../pages/Friends").then((module) => ({ default: module.Friends })),
);
export const FindFriends = React.lazy(() =>
  import("../pages/FindFriends").then((module) => ({
    default: module.FindFriends,
  })),
);
export const PublicProfile = React.lazy(() =>
  import("../pages/PublicProfile").then((module) => ({
    default: module.PublicProfile,
  })),
);
export const Groups = React.lazy(() => import("../pages/Groups"));
export const Invitations = React.lazy(() => import("../pages/Invitations"));
export const PaymentPage = React.lazy(() => import("../pages/PaymentPage"));
export const Referrals = React.lazy(() => import("../pages/Referrals"));

// Public pages - lazy loaded
export const HowItWorks = React.lazy(() => import("../pages/HowItWorks"));
export const Features = React.lazy(() => import("../pages/Features"));
export const Pricing = React.lazy(() => import("../pages/Pricing"));
export const UseCases = React.lazy(() => import("../pages/UseCases"));
export const Accessibility = React.lazy(() => import("../pages/Accessibility"));
export const About = React.lazy(() => import("../pages/About"));
export const Contact = React.lazy(() => import("../pages/Contact"));
export const LoginPage = React.lazy(() => import("../pages/LoginPage"));
export const RegisterPage = React.lazy(() => import("../pages/RegisterPage"));
export const ForgotPassword = React.lazy(
  () => import("../pages/ForgotPassword"),
);
export const PasswordReset = React.lazy(() => import("../pages/PasswordReset"));
export const AuthCallback = React.lazy(() => import("../pages/OAuthCallback"));
export const InvitationPage = React.lazy(
  () => import("../pages/InvitationPage"),
);
export const NotFoundPage = React.lazy(() => import("../pages/NotFoundPage"));
export const SentryTestPage = React.lazy(
  () => import("../pages/SentryTestPage"),
);
export const PushNotificationTest = React.lazy(
  () => import("../pages/PushNotificationTest"),
);
export const NotificationSettings = React.lazy(
  () => import("../pages/NotificationSettings"),
);
export const GuestInvitationWizard = React.lazy(() =>
  import("../components/guest/GuestInvitationWizard").then((module) => ({
    default: module.GuestInvitationWizard,
  })),
);
export const Blog = React.lazy(() => import("../pages/Blog"));
export const OAuthTest = React.lazy(() => import("../pages/OAuthTest"));
export const MapTest = React.lazy(() => import("../pages/MapTest"));
export const Privacy = React.lazy(() => import("../pages/Privacy"));
export const Terms = React.lazy(() => import("../pages/Terms"));
export const DataDeletion = React.lazy(() => import("../pages/DataDeletion"));
export const Unsubscribe = React.lazy(() => import("../pages/Unsubscribe"));
export const HelpCenter = React.lazy(() => import("../pages/HelpCenter"));
export const MobileDemo = React.lazy(() => import("../pages/MobileDemo"));
export const MeetupDetailsMobile = React.lazy(
  () => import("../pages/MeetupDetailsMobile"),
);

// Template pages
export const Templates = React.lazy(() =>
  import("../pages/Templates").then((module) => ({
    default: module.Templates,
  })),
);

// Saved Spots page
export const SavedSpotsPage = React.lazy(() =>
  import("../pages/SavedSpots").then((module) => ({
    default: module.SavedSpotsPage,
  })),
);

// Account pages
export const Account = React.lazy(() => import("../pages/Account/index"));
export const SubscriptionManagement = React.lazy(
  () => import("../pages/Account/SubscriptionManagement"),
);
export const Security = React.lazy(() => import("../pages/Account/Security"));
export const UserAnalytics = React.lazy(
  () => import("../pages/Account/Analytics"),
);

// Admin components - lazy load for better initial bundle size
export const AdminDashboard = React.lazy(
  () => import("../pages/Admin/AdminDashboard"),
);
export const AdminAnalyticsMetrics = React.lazy(() =>
  import("../components/AdminDataView").then((m) => ({
    default: m.AdminAnalyticsMetrics,
  })),
);
export const AdminAnalyticsRevenue = React.lazy(() =>
  import("../components/AdminDataView").then((m) => ({
    default: m.AdminAnalyticsRevenue,
  })),
);
export const AdminAnalyticsChurn = React.lazy(() =>
  import("../components/AdminDataView").then((m) => ({
    default: m.AdminAnalyticsChurn,
  })),
);
export const AdminAnalyticsUsage = React.lazy(() =>
  import("../components/AdminDataView").then((m) => ({
    default: m.AdminAnalyticsUsage,
  })),
);
export const AdminDataUsers = React.lazy(() =>
  import("../components/AdminDataView").then((m) => ({
    default: m.AdminDataUsers,
  })),
);
export const AdminDataMeetups = React.lazy(() =>
  import("../components/AdminDataView").then((m) => ({
    default: m.AdminDataMeetups,
  })),
);
export const AdminUsersSearch = React.lazy(() =>
  import("../components/AdminDataView").then((m) => ({
    default: m.AdminUsersSearch,
  })),
);
export const AdminSystemHealth = React.lazy(() =>
  import("../components/AdminDataView").then((m) => ({
    default: m.AdminSystemHealth,
  })),
);

// Admin User Management - Phase 1
export const UserList = React.lazy(
  () => import("../pages/Admin/UserManagement/UserList"),
);
export const UserDetail = React.lazy(
  () => import("../pages/Admin/UserManagement/UserDetail"),
);
export const UserActivity = React.lazy(
  () => import("../pages/Admin/UserManagement/UserActivity"),
);

// Admin Meetup Moderation - Phase 2
export const PendingMeetups = React.lazy(
  () => import("../pages/Admin/MeetupModeration/PendingMeetups"),
);
export const MeetupDetail = React.lazy(
  () => import("../pages/Admin/MeetupModeration/MeetupDetail"),
);
export const MeetupEdit = React.lazy(
  () => import("../pages/Admin/MeetupModeration/MeetupEdit"),
);
export const ReportedMeetups = React.lazy(
  () => import("../pages/Admin/MeetupModeration/ReportedMeetups"),
);
export const ReportDetail = React.lazy(
  () => import("../pages/Admin/MeetupModeration/ReportDetail"),
);

// Admin System Diagnostics - Phase 3
export const SystemLogs = React.lazy(
  () => import("../pages/Admin/SystemDiagnostics/SystemLogs"),
);
export const ErrorDashboard = React.lazy(
  () => import("../pages/Admin/SystemDiagnostics/ErrorDashboard"),
);
export const PerformanceMetrics = React.lazy(
  () => import("../pages/Admin/SystemDiagnostics/PerformanceMetrics"),
);
export const DatabaseStatus = React.lazy(
  () => import("../pages/Admin/SystemDiagnostics/DatabaseStatus"),
);

// Admin Content Moderation - Phase 5
export const ContentReports = React.lazy(
  () => import("../pages/Admin/ContentModeration/ContentReports"),
);
export const ContentReportDetail = React.lazy(
  () => import("../pages/Admin/ContentModeration/ReportDetail"),
);
export const FlaggedUsers = React.lazy(
  () => import("../pages/Admin/ContentModeration/FlaggedUsers"),
);

// Admin System Configuration - Phase 6
export const SystemConfiguration = React.lazy(
  () => import("../pages/Admin/SystemConfiguration/SystemConfiguration"),
);

// Admin Density Engine - Phase 8
export const DensityEngine = React.lazy(
  () => import("../pages/Admin/DensityEngine"),
);

// Re-export from dedicated module so existing imports continue to work.
// The map itself lives in `routePrefetchMap.ts` to avoid a circular import
// chain via `usePrefetch` -> `lazyPages` -> page modules.
export { routePrefetchMap } from "./routePrefetchMap";
